import React from 'react';
import PropTypes from 'prop-types';

import styles from './Growl.styles.js';
import delay from '../delay.js';
import {
  Checkmark as CheckmarkIcon,
  ExclamationCircle as ExclamationCircleIcon,
  InfoCircle as InfoCircleIcon,
} from '../icons/Icons.jsx';

const growlTimeout = 10 * 1000;

let icons = {
  error: <ExclamationCircleIcon style={styles.badgeIcon} />,
  info: <InfoCircleIcon style={styles.badgeIcon} />,
  success: <CheckmarkIcon style={styles.badgeIcon} />,
};

const GrowlLayout = ({ context, action, boxStyle, icon, message, onClose, style, type }) => (
  <div style={style}>
    <div style={styles.spacer}></div>
    <div style={boxStyle}>
      <div style={styles.container.calculate(type)}>
        <div style={styles.badgeContainer}>{icon}</div>
        <div style={styles.body}>
          {context && <div style={styles.contextContainer} data-test-id="context-section">{context}</div>}
          {message}
          {action}
          <div style={styles.closeButton} onClick={onClose} data-test-id="close">x</div>
        </div>
      </div>
    </div>
  </div>
);

class Growl extends React.PureComponent {
  constructor(props) {
    super(props);

    this.handleClick = this.handleClick.bind(this);
    this.handleClose = this.handleClose.bind(this);

    this.state = {
      actionReplacementElement: null,
    };
  }

  componentDidMount() {
    if (!this.props.action) {
      this.closeAfterTimeout();
    }

    this.waitForCloseScheduled();
  }

  async waitForCloseScheduled() {
    await this.props.closeScheduled;
    this.props.close();
  }

  async closeAfterTimeout() {
    await delay(growlTimeout);
    this.props.close();
  }

  render() {
    const { action, ...layoutProps } = this.props;

    return (
      <GrowlLayout
        {...layoutProps}
        action={this.actionElement}
        icon={icons[this.props.type]}
        onClose={this.handleClose}
      />
    );
  }

  get actionElement() {
    const { action, style } = this.props;

    if (!action) {
      return null;
    }

    let actionElement = this.state.actionReplacementElement
      ? <div style={styles.actionResult}>{this.state.actionReplacementElement}</div>
      : <div style={styles.action} onClick={this.handleClick}>{action.label}</div>;

    return (
      <div style={styles.actionContainer}>{actionElement}</div>
    );
  }

  handleClick() {
    const { action } = this.props;
    if (action.href) {
      window.open(action.href);
    }

    let result = null;
    if (action.onClick) {
      result = action.onClick();
    }

    const { closeMode='immediate', resultElement=null  } = result || {};

    if (this.state.actionReplacementElement !== resultElement) {
      this.setState({ actionReplacementElement: resultElement });
    }

    switch (closeMode) {
    case 'immediate':
      this.props.close({ wasUserInitiated: true });
      return;
    case 'timeout':
      return this.closeAfterTimeout();
    default:
      throw new Error(`Unsupported closeMode: ${closeMode}`);
    }
  }

  handleClose() {
    this.props.onDismiss();
    this.props.close({ wasUserInitiated: true });
  }
}

Growl.propTypes = {
  boxStyle: PropTypes.object,
  close: PropTypes.func.isRequired,
  closeScheduled: PropTypes.object,
  context: PropTypes.string,
  action: PropTypes.object,
  message: PropTypes.any,
  style: PropTypes.object,
  type: PropTypes.oneOf(['error', 'info', 'success']),
  onDismiss: PropTypes.func,
};

Growl.defaultProps = {
  boxStyle: {},
  closeScheduled: new Promise(() => { }),
  onDismiss: () => {}
};

export default Growl;
